import React, { useState, useContext, useEffect } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import IconButton from '@mui/material/IconButton'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen'
import GridViewIcon from '@mui/icons-material/GridView'
import ViewListIcon from '@mui/icons-material/ViewList'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import StarIcon from '@mui/icons-material/Star'
import FavoriteIcon from '@mui/icons-material/Favorite'
import StorefrontIcon from '@mui/icons-material/Storefront'
import PeopleIcon from '@mui/icons-material/People'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import HistoryIcon from '@mui/icons-material/History'
import BookmarkIcon from '@mui/icons-material/Bookmark'
import ExploreIcon from '@mui/icons-material/Explore'
import ReplayIcon from '@mui/icons-material/Replay'
import WbSunnyIcon from '@mui/icons-material/WbSunny'

import ModernProduct from '@/components/Product/ModernProduct'
import { getImageCsCart } from '@/lib/getImageCsCart'
import Link from '@/components/Common/Link'
import getRoutes from '@/lib/getRoutes'
import ProductQtyInput, { getInitialDisplayedQty } from '@/components/Product/ProductQtyInput'
import ProductAddToBasket from '@/components/Product/ProductAddToBasket'
import getProductQuantityPcb from '@/lib/getProductQuantityPcb'
import ProfileContext from '@/components/Context/ProfileContext'
import RecommendReason from '@/components/Recommend/RecommendReason'
import RecommendSlot from '@/components/Recommend/RecommendSlot'

export const DEFAULT_PREVIEW_COUNT = 4

const THEME_CONFIG: Record<string, { icon: React.ReactNode; bg: string; border: string }> = {
  trending: { icon: <TrendingUpIcon sx={{ fontSize: 18 }} />, bg: '#f9fefb', border: '#e2f0e8' },
  bestsellers: { icon: <StarIcon sx={{ fontSize: 18 }} />, bg: '#fefef8', border: '#f5f0d8' },
  smart: { icon: <FavoriteIcon sx={{ fontSize: 18 }} />, bg: '#fdfaff', border: '#efe6f5' },
  'company-bestsellers': { icon: <StorefrontIcon sx={{ fontSize: 18 }} />, bg: '#f8fbff', border: '#e4edf8' },
  'also-bought': { icon: <PeopleIcon sx={{ fontSize: 18 }} />, bg: '#fef9fb', border: '#f3e6ee' },
  related: { icon: <AutoAwesomeIcon sx={{ fontSize: 18 }} />, bg: '#fbfaff', border: '#ece8f5' },
  fbt: { icon: <ShoppingCartIcon sx={{ fontSize: 18 }} />, bg: '#fffcf8', border: '#f5edd8' },
  recent: { icon: <HistoryIcon sx={{ fontSize: 18 }} />, bg: '#f8fcff', border: '#e0eef8' },
  wishlist: { icon: <BookmarkIcon sx={{ fontSize: 18 }} />, bg: '#fffafa', border: '#f5e6e6' },
  reorder: { icon: <ReplayIcon sx={{ fontSize: 18 }} />, bg: '#f8fcff', border: '#e0eef8' },
  personalized: { icon: <AutoAwesomeIcon sx={{ fontSize: 18 }} />, bg: '#fbfaff', border: '#ece8f5' },
  discovery: { icon: <ExploreIcon sx={{ fontSize: 18 }} />, bg: '#f9fefb', border: '#e2f0e8' },
  season: { icon: <WbSunnyIcon sx={{ fontSize: 18 }} />, bg: '#fffcf8', border: '#f5edd8' },
}

type Props = {
  title: string
  /** Pourquoi ce bloc, sous le titre */
  subtitle?: string
  products: any[]
  previewCount?: number
  tooltip?: string
  expanded?: boolean
  onToggle?: () => void
  theme?: string
  defaultListView?: boolean
  /** Emplacement du bloc pour la mesure (ex. home:bestsellers). Sans lui, rien n'est mesuré. */
  placement?: string
}

/**
 * Ligne compacte : image 48 px, marque, nom, EAN, raison de la proposition, prix, puis quantité et panier.
 * En tuile (bloc franco du panier), le cadre vient du parent : pas de trait bas, nom sur deux lignes.
 */
export const ProductListItem: React.FC<{ product: any; theme?: string; tile?: boolean }> = ({
  product,
  theme,
  tile = false,
}) => {
  const slug = product.seo_name || product.product_id
  const url = getRoutes({ route: 'Product', values: { '[slug]': slug } })
  const imgSrc = getImageCsCart(product.image_pairs?.[0] || product.main_pair)
  const { isAuthenticated } = useContext(ProfileContext)
  const [qtyCart, setQtyCart] = useState<number>(0)

  useEffect(() => {
    if (product) {
      const qty = getProductQuantityPcb(product, 0)
      setQtyCart(Number.isFinite(qty) && qty > 0 ? qty : getInitialDisplayedQty(product))
    }
  }, [product?.seo_name])

  return (
    <Box
      sx={{
        borderBottom: tile ? 'none' : '1px solid',
        borderColor: 'divider',
        height: tile ? '100%' : undefined,
        display: tile ? 'flex' : undefined,
        flexDirection: tile ? 'column' : undefined,
        justifyContent: tile ? 'space-between' : undefined,
        '&:hover': { bgcolor: '#f9f9f9' },
      }}
    >
      <Link href={url.href} as={url.as} style={{ textDecoration: 'none', color: 'inherit' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, px: 1 }}>
          <img
            src={imgSrc}
            alt={product.product || ''}
            width={48}
            height={48}
            style={{ objectFit: 'contain', flexShrink: 0 }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {product.brand_name && (
              <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#4CAF50' }}>
                {product.brand_name}
              </Typography>
            )}
            <Typography
              title={product.product}
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: '#213390',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                ...(tile
                  ? { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }
                  : { whiteSpace: 'nowrap' }),
              }}
            >
              {product.product}
            </Typography>
            {product.product_code && (
              <Typography sx={{ fontSize: 10, color: 'text.secondary', fontStyle: 'italic' }}>
                {product.product_code}
              </Typography>
            )}
            {/* Vendeur en texte simple : la ligne entiere est deja un lien. En tuile (franco du
                panier), le titre du bloc dit deja que c'est le meme vendeur. */}
            {!tile && product.company_name && (
              <Typography sx={{ fontSize: 10, color: 'text.secondary' }} noWrap>
                {t({ id: 'web_vendu_par' })} : {product.company_name}
              </Typography>
            )}
            <RecommendReason product={product} theme={theme} flush />
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#F9A826', whiteSpace: 'nowrap' }}>
            {parseFloat(product.price || '0').toFixed(2).replace('.', ',')} €
            <span style={{ fontSize: 10, fontWeight: 400 }}> HT</span>
          </Typography>
        </Box>
      </Link>

      {/* Ligne d'ajout panier : reutilise le selecteur + bouton de la carte colonne (ModernProduct).
          Place HORS du <Link> pour ne pas declencher la navigation au clic. */}
      {!!isAuthenticated && (
        <Box sx={{ px: 1, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <ProductQtyInput
              product={product}
              initialQty={qtyCart || getProductQuantityPcb(product, 0) || 1}
              onChange={setQtyCart}
              displayMode="row"
              showInfoBar={false}
              compact
            />
          </Box>
          <ProductAddToBasket qtyCart={qtyCart} product={product} list compactMode compact />
        </Box>
      )}
    </Box>
  )
}

const RecommendCard: React.FC<Props> = ({
  title,
  subtitle,
  products,
  previewCount = DEFAULT_PREVIEW_COUNT,
  tooltip,
  expanded = false,
  onToggle,
  theme,
  defaultListView = false,
  placement,
}) => {
  const [listView, setListView] = useState(defaultListView)
  const themeConfig = theme ? THEME_CONFIG[theme] : undefined

  if (!products || products.length === 0) return null

  const displayProducts = expanded ? products : products.slice(0, previewCount)

  return (
    <Box
      sx={{
        bgcolor: themeConfig?.bg || 'background.paper',
        borderRadius: 3,
        border: '1px solid',
        borderColor: themeConfig?.border || 'divider',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s ease',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          pt: 1.5,
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: expanded ? 'flex-start' : 'space-between',
          gap: expanded ? 2 : 0,
          ...(expanded ? { position: 'sticky', top: 0, zIndex: 2, bgcolor: themeConfig?.bg || 'background.paper', borderBottom: '1px solid', borderColor: 'divider' } : {}),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {themeConfig?.icon && (
            <Box sx={{ color: '#213390', display: 'flex' }}>{themeConfig.icon}</Box>
          )}
          <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#213390' }}>{title}</Typography>
          {products.length > previewCount && (
            <Typography
              component="span"
              sx={{ fontSize: 13, color: 'text.secondary', fontWeight: 400 }}
            >
              ({products.length})
            </Typography>
          )}
          {tooltip && (
            <Tooltip title={tooltip} arrow placement="top">
              <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
            </Tooltip>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {/* View toggle */}
          <IconButton
            size="small"
            onClick={() => setListView(false)}
            sx={{ color: !listView ? '#213390' : 'text.disabled', p: 0.5 }}
          >
            <GridViewIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => setListView(true)}
            sx={{ color: listView ? '#213390' : 'text.disabled', p: 0.5 }}
          >
            <ViewListIcon sx={{ fontSize: 18 }} />
          </IconButton>
          {/* Voir tout / Réduire */}
          {!expanded && products.length > previewCount && onToggle && (
            <Box
              component="button"
              onClick={onToggle}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                fontSize: 13,
                fontWeight: 600,
                color: '#213390',
                bgcolor: '#f0f2fa',
                border: '1px solid #d6daf0',
                borderRadius: 2,
                px: 1.5,
                py: 0.5,
                ml: 0.5,
                cursor: 'pointer',
                '&:hover': { bgcolor: '#e0e4f5' },
              }}
            >
              Voir tout <OpenInFullIcon sx={{ fontSize: 14 }} />
            </Box>
          )}
          {expanded && onToggle && (
            <Box
              component="button"
              onClick={onToggle}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                fontSize: 13,
                fontWeight: 600,
                color: '#e74c3c',
                bgcolor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: 2,
                px: 1.5,
                py: 0.5,
                ml: 0.5,
                cursor: 'pointer',
                '&:hover': { bgcolor: '#fde8e8' },
              }}
            >
              Réduire <CloseFullscreenIcon sx={{ fontSize: 14 }} />
            </Box>
          )}
        </Box>
      </Box>

      {subtitle && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1.5, pb: 0.5 }}>
          {subtitle}
        </Typography>
      )}

      {/* Products — grid or list */}
      {listView ? (
        <Box
          sx={{
            px: 1,
            pb: 1,
            flex: 1,
            ...(expanded ? { maxHeight: '70vh', overflowY: 'auto' } : {}),
          }}
        >
          {displayProducts.map((product, index) => (
            <RecommendSlot
              key={product.product_id || product.seo_name}
              placement={placement}
              productId={product.product_id}
              rank={index + 1}
            >
              <ProductListItem product={product} theme={theme} />
            </RecommendSlot>
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            px: 1,
            pb: 1,
            flex: 1,
            display: 'grid',
            gridTemplateColumns: expanded
              ? { xs: 'repeat(2, 1fr)', md: 'repeat(6, 1fr)' }
              : { xs: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)' },
            gap: 1,
            ...(expanded ? { maxHeight: '70vh', overflowY: 'auto' } : {}),
          }}
        >
          {displayProducts.map((product, index) => (
            <Box key={product.product_id || product.seo_name} sx={{ maxWidth: 280, mx: 'auto' }}>
              <RecommendSlot
                placement={placement}
                productId={product.product_id}
                rank={index + 1}
                className="h-full"
              >
                <ModernProduct product={product} sizeProduct="small" />
                <RecommendReason product={product} theme={theme} />
              </RecommendSlot>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}

export default RecommendCard
